import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from "firebase/firestore";
import { createWithId, getAll, getById, remove, update, updateField } from "../utils/dbUtils";
import { db } from "../firebase";
import { asTimeZone } from "../utils/dateFormatter";
import moment from "moment-timezone";
import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import Enums from "../utils/enums";

const normalizeStringList = (value) => {
    return Array.isArray(value) ? Array.from(new Set(value.filter((item) => isStringNotEmpty(item)))) : [];
};

const normalizeNumber = (value, fallback = 0) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const normalizeOption = (option = {}) => {
    return {
        offerGroupId: isStringNotEmpty(option.offerGroupId) ? option.offerGroupId : null,
        alternateOfferGroupIds: normalizeStringList(option.alternateOfferGroupIds),
        productIds: normalizeStringList(option.productIds),
        productSkus: normalizeStringList(option.productSkus),
        entryTokens: normalizeStringList(option.entryTokens),
        displayOrder: normalizeNumber(option.displayOrder),
        // Enum FunnelChoiceProductResolutionModes
        productResolutionMode: isStringNotEmpty(option.productResolutionMode) ? option.productResolutionMode : 'CANONICAL_BY_DISPLAY_ORDER',
        badgeLabel: isStringNotEmpty(option.badgeLabel) ? option.badgeLabel : null,
        noticeMessage: isStringNotEmpty(option.noticeMessage) ? option.noticeMessage : null,
        allowedGenders: normalizeStringList(option.allowedGenders),
        unavailableMessage: isStringNotEmpty(option.unavailableMessage) ? option.unavailableMessage : null,
        // Enum FunnelChoiceDefaultVariantStrategies
        defaultVariantStrategy: isStringNotEmpty(option.defaultVariantStrategy) ? option.defaultVariantStrategy : 'DISPLAY_ORDER',
    };
};

const normalizeSection = (section = {}) => {
    const options = isListNotEmpty(section.options)
        ? section.options.map(normalizeOption).filter((option) => isStringNotEmpty(option.offerGroupId))
        : [];

    return {
        key: isStringNotEmpty(section.key) ? section.key : null,
        label: isStringNotEmpty(section.label) ? section.label : null,
        displayOrder: normalizeNumber(section.displayOrder),
        defaultVisible: isBooleanTrue(section.defaultVisible),
        options: options.sort((a, b) => a.displayOrder - b.displayOrder),
    };
};

const getDuplicateValues = (values = []) => {
    const seenValues = new Set();
    const duplicateValues = new Set();
    values.filter((value) => isStringNotEmpty(value)).forEach((value) => {
        if (seenValues.has(value)) {
            duplicateValues.add(value);
        } else {
            seenValues.add(value);
        }
    });

    return Array.from(duplicateValues);
};

class FunnelChoiceSet {
    static collectionName = 'funnelChoiceSets';

    id = null;
    name = null;
    enabled = false;
    programId = null;
    defaultForProgram = false;
    defaultOfferGroupId = null;
    entryOfferGroupIds = [];
    // Enum FunnelChoiceSetRenderModes
    renderMode = 'PRODUCT_CARDS';
    sections = [];

    constructor(choiceSet) {
        if (isObjectNotEmpty(choiceSet)) {
            if (isStringNotEmpty(choiceSet.id)) { this.id = choiceSet.id; }
            if (isStringNotEmpty(choiceSet.name)) { this.name = choiceSet.name; }
            if (isBooleanTrue(choiceSet.enabled)) { this.enabled = true; }
            if (isStringNotEmpty(choiceSet.programId)) { this.programId = choiceSet.programId; }
            if (isBooleanTrue(choiceSet.defaultForProgram)) { this.defaultForProgram = true; }
            if (isStringNotEmpty(choiceSet.defaultOfferGroupId)) { this.defaultOfferGroupId = choiceSet.defaultOfferGroupId; }
            if (isListNotEmpty(choiceSet.entryOfferGroupIds)) { this.entryOfferGroupIds = normalizeStringList(choiceSet.entryOfferGroupIds); }
            if (isStringNotEmpty(choiceSet.renderMode)) { this.renderMode = choiceSet.renderMode; }
            if (isListNotEmpty(choiceSet.sections)) {
                this.sections = choiceSet.sections.map(normalizeSection).filter((section) => isStringNotEmpty(section.key));
            }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            enabled: this.enabled,
            programId: this.programId,
            defaultForProgram: this.defaultForProgram,
            defaultOfferGroupId: this.defaultOfferGroupId,
            entryOfferGroupIds: this.entryOfferGroupIds,
            renderMode: this.renderMode,
            sections: this.sections,
        };
    }

    isValid = () => {
        if (isStringEmpty(this.id)) {
            throw new Error('Choice set code is required.');
        }
        if (isStringEmpty(this.name)) {
            throw new Error('Name is required.');
        }
        if (isStringEmpty(this.programId)) {
            throw new Error('Program is required.');
        }
        if (isStringEmpty(this.defaultOfferGroupId)) {
            throw new Error('Default offer group is required.');
        }
        if (isStringEmpty(this.renderMode)) {
            throw new Error('Render mode is required.');
        }
        if (!Enums.FunnelChoiceSetRenderModes.includesKey(this.renderMode)) {
            throw new Error('Render mode must be a valid FunnelChoiceSetRenderModes enum value.');
        }
        if (!this.entryOfferGroupIds.includes(this.defaultOfferGroupId)) {
            throw new Error('Default offer group must be included in entry offer groups.');
        }
        if (!isListNotEmpty(this.sections)) {
            throw new Error('At least one section is required.');
        }

        const duplicateSectionKeys = getDuplicateValues(this.sections.map((section) => section.key));
        if (isListNotEmpty(duplicateSectionKeys)) {
            throw new Error(`Section keys must be unique. Duplicate section key(s): ${duplicateSectionKeys.join(', ')}.`);
        }

        const duplicateEntryTokens = getDuplicateValues(this.sections.flatMap((section) => {
            return (section.options ?? []).flatMap((option) => option.entryTokens ?? []);
        }));
        if (isListNotEmpty(duplicateEntryTokens)) {
            throw new Error(`Entry token aliases must be unique within a funnel choice set. Duplicate token(s): ${duplicateEntryTokens.join(', ')}.`);
        }

        this.sections.forEach((section) => {
            if (isStringEmpty(section.key)) {
                throw new Error('Every section requires a key.');
            }
            if (isStringEmpty(section.label)) {
                throw new Error(`Section ${section.key} requires a label.`);
            }
            if (!isListNotEmpty(section.options)) {
                throw new Error(`Section ${section.key} requires at least one option.`);
            }
            section.options.forEach((option) => {
                if (!this.entryOfferGroupIds.includes(option.offerGroupId)) {
                    throw new Error(`Option ${option.offerGroupId} must be included in entry offer groups.`);
                }
                if (!Enums.FunnelChoiceProductResolutionModes.includesKey(option.productResolutionMode)) {
                    throw new Error(`Option ${option.offerGroupId} has an invalid product resolution mode.`);
                }
                if (!Enums.FunnelChoiceDefaultVariantStrategies.includesKey(option.defaultVariantStrategy)) {
                    throw new Error(`Option ${option.offerGroupId} has an invalid default variant strategy.`);
                }
                if (option.alternateOfferGroupIds.includes(option.offerGroupId)) {
                    throw new Error(`Option ${option.offerGroupId} cannot include itself as an alternate offer group.`);
                }
            });
        });

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await createWithId(FunnelChoiceSet.collectionName, this.id, this.toDto());
        }

        return null;
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(FunnelChoiceSet.collectionName, id, fieldName, newValue);
        }

        throw new Error("id is required.");
    }

    static update = async (id, updatedChoiceSet, oldObj) => {
        const updatedObj = new FunnelChoiceSet(updatedChoiceSet);
        if (updatedObj.isValid() && id === updatedChoiceSet.id) {
            const success = await update(FunnelChoiceSet.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldObj, ...updatedObj.toDto() };
            }
        } else {
            throw new Error('id and id field of the updated funnel choice set must match.');
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(FunnelChoiceSet.collectionName, id);
        }

        throw new Error("id is required.");
    }

    static getAll = async () => {
        return await getAll(FunnelChoiceSet.collectionName);
    }

    static getById = async (id) => {
        return await getById(FunnelChoiceSet.collectionName, id);
    }

    static getCount = async (queryParameters = {}) => {
        const { start, end } = queryParameters;
        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            const startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            const endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        const q = query(collection(db, FunnelChoiceSet.collectionName), ...queryConstraints);
        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters = {}, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { start, end } = queryParameters;
        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            const startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            const endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, FunnelChoiceSet.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, FunnelChoiceSet.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, FunnelChoiceSet.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        const snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
        }));
    }
}

export default FunnelChoiceSet;
